import { RowData, Table as TanStackTable } from "@tanstack/react-table";
import { Button, IconButtonV2 } from "../../../primitives";
import { cn } from "../../../utils/classes";
import styles from "../table.module.css";

const isPaginationEnabled = <D extends RowData>(
  table: TanStackTable<D>,
): boolean => {
  const manualPagination = table.options.manualPagination;
  if (manualPagination) {
    return true;
  }

  return table.options.getPaginationRowModel !== undefined;
};

type PageType = "page" | "ellipsis" | "current";
export function getPaginationItems<V>(
  totalPages: number,
  currentPageIndex: number,
  item: (page: number, type: PageType, index: number) => V,
  maxNumberItemsShown: number = 7,
): V[] {
  const lastIndex = Math.max(0, totalPages - 1);
  // const currentPageIndex = clamp(currentPageIndex, 0, lastIndex);

  if (totalPages <= maxNumberItemsShown)
    return Array.from({ length: lastIndex + 1 }, (_, i) =>
      item(i + 1, i === currentPageIndex ? "current" : "page", i),
    );

  const tokens: number[] = [];

  const push = (v: number) => {
    if (tokens.length === 0 || tokens[tokens.length - 1] !== v) tokens.push(v);
  };

  push(0);

  if (currentPageIndex <= 2) {
    for (let p = 1; p <= Math.min(4, lastIndex - 1); p++) push(p);
    if (lastIndex > 5) push(-1);
  } else if (currentPageIndex >= lastIndex - 2) {
    if (lastIndex > 5) push(-1);
    for (let p = Math.max(1, lastIndex - 4); p <= lastIndex - 1; p++) push(p);
  } else {
    push(-1);
    push(currentPageIndex - 1);
    push(currentPageIndex);
    push(currentPageIndex + 1);
    push(-1);
  }

  push(lastIndex);

  return tokens.map((t, i) => {
    if (t === -1) return item(-1, "ellipsis", i);
    return item(t + 1, t === currentPageIndex ? "current" : "page", i);
  });
}

export const Pagination = <D extends RowData>({
  table,
}: {
  table: TanStackTable<D>;
}) => {
  if (!table) {
    return null;
  }

  const isEnabled = isPaginationEnabled(table);

  if (!isEnabled) {
    return null;
  }
  const pageIndex = table.getState().pagination.pageIndex;
  const totalPages = table.getPageCount() || 1;

  const previousPage = () =>
    pageIndex > 0 ? table.setPageIndex(pageIndex - 1) : void 0;
  const nextPage = () =>
    pageIndex < totalPages - 1 ? table.setPageIndex(pageIndex + 1) : void 0;

  return (
    <>
      <div
        className={cn(
          "sticky inset-x-0 bottom-1 top-0 mt-4 flex h-8 w-full flex-1 select-none items-center justify-center gap-x-2 bg-white text-neutral-800 hover:text-neutral-800 gap-3",
          styles.pagination,
        )}
      >
        <IconButtonV2
          size="sm"
          className="font-normal whitespace-nowrap"
          variant="secondary"
          onClick={() => previousPage()}
          icon={"Chevron Left"}
          disabled={pageIndex === 0}
        />
        {getPaginationItems(totalPages, pageIndex, (page, type, index) => (
          <Button
            key={`p-${index}`}
            size="sm"
            className="font-normal whitespace-nowrap bg-transparent"
            variant="secondary"
            disabled={type !== "page"}
            onClick={type === "page" ? () => table.setPageIndex(page - 1) : undefined}
          >
            {type === "ellipsis" ? "..." : page}
          </Button>
        ))}
        <IconButtonV2
          size="sm"
          className="font-normal whitespace-nowrap"
          variant="secondary"
          onClick={() => nextPage()}
          icon={"Chevron Right"}
          disabled={pageIndex === totalPages - 1}
        />
      </div>
    </>
  );
};

export type PaginationOptions = {
  pageSize?: number;
  enabled?: boolean;
  mode?: "client" | "server";
  /** Zero-based active page. Required when mode is server. */
  pageIndex?: number;
  /** Total pages reported by the server. */
  pageCount?: number;
  /** Total rows reported by the server. */
  rowCount?: number;
  onPageChange?: (pageIndex: number) => void;
};
